/**
 * /contact — Page dédiée à la prise de contact (formulaire + coordonnées).
 * Distincte de /reserver qui est plus orientée réservation immédiate.
 */
import React, { useState } from "react";
import { Mail, MapPin, Send, CheckCircle2, Instagram, Youtube, User } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "../components/Layout";
import api, { formatApiErrorDetail } from "../lib/api";
import { SITE } from "../lib/site";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Merci de remplir le nom, l'email et votre message.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message envoyé. Valentine vous répondra rapidement.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Envoi impossible. Réessayez.");
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <Seo
        title="Me contacter"
        description="Envoyer un message à Valentine DL — hypnose régressive et spirituelle à Pantin (15 min de Paris) et en ligne. Une question, une envie d'échanger avant une séance."
        canonical="https://www.parfaitsimparfaits.fr/contact"
      />

      <section className="pt-40 pb-12" data-testid="contact-page-hero">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <p className="eyebrow mb-5">Prendre contact</p>
          <h1 className="text-4xl sm:text-5xl font-serif font-light leading-tight text-sand-900">
            Une question, une envie d'échanger&nbsp;?
            <br />
            <em className="italic font-normal text-[#B07A45] inline-block mt-2">Écrivez-moi.</em>
          </h1>
          <p className="mt-6 text-lg text-sand-700 max-w-2xl leading-relaxed">
            Vous pouvez m'écrire par e-mail ou déposer un message ci-dessous.
            Je vous réponds personnellement, sous 24&nbsp;h ouvrées.
          </p>
        </div>
      </section>

      <section className="py-16" data-testid="contact-page-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Coordonnées */}
          <aside className="md:col-span-5 space-y-6" data-testid="contact-page-info">
            <div className="rounded-2xl bg-sand-200/40 p-8 border border-sand-300/60">
              <p className="eyebrow mb-4">Coordonnées</p>
              <ul className="space-y-5 text-sand-800">
                <li className="flex gap-4">
                  <User className="text-sand-500 mt-1" size={20} />
                  <div>
                    <p className="text-sm text-sand-600">Praticienne</p>
                    <p className="text-lg font-medium">Valentine DL</p>
                  </div>
                </li>
                <li className="flex gap-4">
                  <Mail className="text-sand-500 mt-1" size={20} />
                  <div>
                    <p className="text-sm text-sand-600">Email</p>
                    <a href="mailto:v.parfaitsimparfaits@gmail.com" data-testid="contact-page-email" className="text-lg font-medium hover:text-sand-500 break-all">
                      v.parfaitsimparfaits@gmail.com
                    </a>
                  </div>
                </li>
                <li className="flex gap-4">
                  <MapPin className="text-sand-500 mt-1" size={20} />
                  <div>
                    <p className="text-sm text-sand-600">Où me rencontrer</p>
                    <p className="text-lg font-medium leading-snug">
                      Séances en présentiel à Pantin
                      <br />
                      <span className="text-sand-700 text-base font-normal">
                        Métro Église de Pantin, ligne 5 (15 min de Paris)
                      </span>
                      <br />
                      et séances en ligne, en visio
                    </p>
                  </div>
                </li>
                {SITE.instagram && (
                  <li className="flex gap-4">
                    <Instagram className="text-sand-500 mt-1" size={20} />
                    <div>
                      <p className="text-sm text-sand-600">Instagram</p>
                      <a
                        href={SITE.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="contact-page-instagram"
                        className="text-lg font-medium hover:text-sand-500"
                      >
                        @parfaits_imparfaits
                      </a>
                    </div>
                  </li>
                )}
                {SITE.youtube && (
                  <li className="flex gap-4">
                    <Youtube className="text-sand-500 mt-1" size={20} />
                    <div>
                      <p className="text-sm text-sand-600">YouTube</p>
                      <a
                        href={SITE.youtube}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="contact-page-youtube"
                        className="text-lg font-medium hover:text-sand-500"
                      >
                        @ParfaitsImparfaits
                      </a>
                    </div>
                  </li>
                )}
              </ul>
            </div>
            <div className="rounded-2xl bg-sand-900 text-sand-100 p-8">
              <p className="font-serif italic text-2xl leading-snug">
                « Un premier échange, sans engagement, permet souvent d'y voir plus clair. »
              </p>
              <p className="mt-6 text-sand-500 eyebrow !text-sand-500">Valentine DL</p>
            </div>
          </aside>

          {/* Formulaire */}
          <div className="md:col-span-7">
            {sent ? (
              <div className="rounded-2xl bg-sand-200/40 border border-sand-300/60 p-12 text-center" data-testid="contact-page-success">
                <CheckCircle2 className="mx-auto text-sand-500 mb-5" size={48} />
                <h2 className="text-3xl font-serif text-sand-900">Message envoyé</h2>
                <p className="mt-4 text-sand-700">
                  Merci pour votre message. Valentine vous répondra dès que possible.
                </p>
                <button onClick={() => setSent(false)} data-testid="contact-page-reset" className="btn-outline mt-8">
                  Envoyer un autre message
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-6" data-testid="contact-page-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Nom" name="name" testid="contact-page-input-name" value={form.name} onChange={(v) => set("name", v)} required />
                  <Field label="Email" name="email" type="email" testid="contact-page-input-email" value={form.email} onChange={(v) => set("email", v)} required />
                </div>
                <Field label="Téléphone (optionnel)" name="phone" testid="contact-page-input-phone" value={form.phone} onChange={(v) => set("phone", v)} />
                <div>
                  <label className="block text-sm text-sand-700 mb-2">Votre message</label>
                  <textarea
                    value={form.message}
                    onChange={(e) => set("message", e.target.value)}
                    rows={6}
                    required
                    data-testid="contact-page-input-message"
                    className="w-full rounded-xl border border-sand-400 bg-white/70 px-5 py-4 text-sand-900 placeholder:text-sand-500 focus:outline-none focus:border-sand-500 transition-colors"
                    placeholder="Une question, un doute, une envie d'en savoir plus… n'hésitez pas."
                  />
                </div>
                <button type="submit" disabled={sending} data-testid="contact-page-submit" className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed">
                  {sending ? "Envoi…" : (<>Envoyer le message <Send size={16} /></>)}
                </button>
                <p className="text-xs text-sand-500">
                  Vos informations restent strictement confidentielles et ne servent qu'à vous répondre.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, name, type = "text", value, onChange, required, testid }) {
  return (
    <div>
      <label htmlFor={name} className="block text-sm text-sand-700 mb-2">{label}{required && " *"}</label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="w-full rounded-xl border border-sand-400 bg-white/70 px-5 py-3 text-sand-900 placeholder:text-sand-500 focus:outline-none focus:border-sand-500 transition-colors"
      />
    </div>
  );
}
